/**
 * AgenticPaymentReferral - Entry Point
 *
 * Top-level component mounted by the Rails ERB partial.
 * Styled using the shared AgenticPayment design system (styles.js).
 */

import React, { useState } from 'react';
import { VIEWS } from './constants';
import { colors, gradients, pageHeaderStyles } from '../AgenticPaymentTransactionHistory/styles';
import ReferralDashboard from './ReferralDashboard';
import ReferralList      from './ReferralList';
import ReferralDetail    from './ReferralDetail';
import PaymentHistory    from './PaymentHistory';
import MonthlyPayout     from './MonthlyPayout';
import './styles.scss';

const TABS = [
  { view: VIEWS.LIST,            label: 'Referrals' },
  { view: VIEWS.PAYMENT_HISTORY, label: 'Payment History' },
  { view: VIEWS.MONTHLY_PAYOUT,  label: 'Monthly Payout' },
];

const AgenticPaymentReferral = ({ adminUserId = '' }) => {
  const [activeView, setActiveView]             = useState(VIEWS.LIST);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [tabHover, setTabHover]                 = useState(null);

  return (
    <div className="liquidity-pool-referral">

      {/* Page Header, same gradient as TransactionHistory */}
      <div style={pageHeaderStyles.container}>
        <h1 style={pageHeaderStyles.title}>Referral Rewards</h1>
        <p style={pageHeaderStyles.subtitle}>
          Manage referral reward payouts, review eligibility, and configure reward settings
        </p>
      </div>

      <div style={{ padding: '24px 0' }}>

        {/* Dashboard stats, always visible */}
        <ReferralDashboard />

        {/* Navigation Tabs */}
        <div style={{ paddingTop: '12px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '4px', borderBottom: `2px solid ${colors.border}` }}>
            {TABS.map((tab) => {
              const isActive = activeView === tab.view;
              const isHovered = tabHover === tab.view && !isActive;
              return (
                <div
                  key={tab.view}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveView(tab.view)}
                  onKeyDown={(e) => e.key === 'Enter' && setActiveView(tab.view)}
                  onMouseEnter={() => setTabHover(tab.view)}
                  onMouseLeave={() => setTabHover(null)}
                  data-testid={`tab-${tab.view}`}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '4px 4px 0 0',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    letterSpacing: '0.2px',
                    lineHeight: '1.4',
                    marginBottom: '-2px',
                    userSelect: 'none',
                    background: isActive
                      ? gradients.primary
                      : isHovered
                        ? colors.bgMedium
                        : colors.gray100,
                    color: isActive ? '#ffffff' : isHovered ? colors.primary : colors.gray600,
                    borderBottom: isActive ? `2px solid ${colors.primary}` : '2px solid transparent',
                  }}
                >
                  {tab.label}
                </div>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        {activeView === VIEWS.LIST && (
          <ReferralList
            adminUserId={adminUserId}
            onViewDetail={(referral) => setSelectedReferral(referral)}
          />
        )}
        {activeView === VIEWS.PAYMENT_HISTORY && <PaymentHistory />}
        {activeView === VIEWS.MONTHLY_PAYOUT && <MonthlyPayout adminUserId={adminUserId} />}
      </div>

      {/* Detail Modal */}
      {selectedReferral && (
        <ReferralDetail
          referral={selectedReferral}
          adminUserId={adminUserId}
          onClose={() => setSelectedReferral(null)}
        />
      )}
    </div>
  );
};

export default AgenticPaymentReferral;
